import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useHistory, useParams } from 'react-router-dom';
import { Button, CircularProgress, Grid } from '@material-ui/core';
import { Delete as DeleteIcon } from '@material-ui/icons';

import { FetchError } from '../../common/api/api';
import AppSnackbar from '../../common/AppSnackbar';
import ConfirmDialog from '../../common/dialogs/ConfirmDialog';
import PageTemplate from '../../common/page-template/PageTemplate';
import PageTemplateDashboard from '../../common/page-template/PageTemplateDashboard';
import PageTemplateHeader, {
  PageTemplateHeaderActions
} from '../../common/page-template/PageTemplateHeader';
import GroupInfoCard from './components/GroupInfoCard';
import GroupUsersCard from './components/GroupUsersCard';
import { useDeleteGroup, useFindGroup } from './Group.hooks';
import GroupRolesCard from './components/GroupRolesCard';

const StyledGrid = styled(Grid)`
  flex: 1;
  .grid-flex,
  .card-flex,
  .card-content-flex {
    flex: 1;
    display: flex;
    flex-direction: column;
  }
`;

const GroupDetailPage: React.FC = () => {
  const history = useHistory();
  const { groupId: paramGroupId = '' } = useParams<{ groupId?: string }>();
  const groupId: number = parseInt(paramGroupId) || 0;
  const fetchUsersRef = useRef<Function | undefined>();
  const [error, setError] = useState<FetchError | undefined>();
  const [isOpenDialog, setIsOpenDialog] = useState<boolean>(false);
  const { group, isLoading: isFinding, error: findError } = useFindGroup(
    groupId,
    true
  );
  const {
    group: deletedGroup,
    isLoading: isDeleting,
    error: deleteError,
    del
  } = useDeleteGroup(groupId);
  const apiError: FetchError | undefined = findError || deleteError;

  useEffect(() => {
    if (deletedGroup) {
      history.push('/portal/groups');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, deletedGroup]);

  useEffect(() => {
    setError(apiError);
  }, [apiError]);

  return (
    <PageTemplate>
      <PageTemplateHeader>
        <h2>Team Detail</h2>
        <PageTemplateHeaderActions flex>
          <Button
            color='primary'
            variant='contained'
            className='flex-label'
            disabled={isDeleting || isFinding || !group}
            onClick={() => setIsOpenDialog(true)}
            endIcon={
              isDeleting ? (
                <CircularProgress color='primary' size={20} />
              ) : (
                <DeleteIcon />
              )
            }
          >
            Delete
          </Button>
        </PageTemplateHeaderActions>
      </PageTemplateHeader>
      <PageTemplateDashboard>
        <StyledGrid container spacing={3}>
          <Grid item xs={12} md={8} className='grid-flex'>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <GroupInfoCard groupId={+groupId} />
              </Grid>
              <Grid item xs={12}>
                <GroupUsersCard groupId={+groupId} fetchRef={fetchUsersRef} />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={4} className='grid-flex'>
            <GroupRolesCard
              groupId={+groupId}
              onUpdate={() => fetchUsersRef.current?.()}
            />
          </Grid>
        </StyledGrid>
      </PageTemplateDashboard>

      <ConfirmDialog
        confirmLabel='Delete'
        open={isOpenDialog && !!group}
        title={`Delete Team: ${group?.name}`}
        message='Are you sure you want to delete this team? This action cannot be undone.'
        onCancel={() => setIsOpenDialog(false)}
        onConfirm={() => {
          setIsOpenDialog(false);
          del();
        }}
      />

      <AppSnackbar severity='error' open={!!error}>
        {error?.message}
      </AppSnackbar>
    </PageTemplate>
  );
};

export default GroupDetailPage;
